import React, { useState } from "react";
import { Bell, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useT } from "../context/LanguageContext";
import { toast } from "sonner";

/**
 * Compact "send reminder" icon button for an admin/mod to nudge a player who
 * hasn't submitted a score. The backend picks the next level (1→2→3 cap),
 * so the UI stays simple. Pass `currentLevel` to display "MAX" when at L3.
 */
export default function SendReminderButton({
  matchId,
  userId,
  username,
  currentLevel = 0,
  onSent,
  size = "md",
  className = "",
}) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/matches/${matchId}/remind/${userId}`);
      toast.success(t("rem.sent", { level: data.level, name: username || "player" }));
      if (typeof onSent === "function") onSent(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const isMaxed = currentLevel >= 3;
  const sizeCls = size === "sm" ? "px-2 py-1 text-[9px]" : "px-3 py-1.5 text-[10px]";
  const nextLevel = Math.min(3, (currentLevel || 0) + 1);
  const accent = nextLevel === 3 ? "#E11D48" : nextLevel === 2 ? "#EA580C" : "#7C3AED";

  return (
    <button
      type="button"
      onClick={send}
      disabled={busy}
      data-testid={`reminder-btn-${matchId}-${userId}`}
      title={isMaxed ? t("rem.maxedOut") : `${t("rem.send")} · L${nextLevel}`}
      aria-label={t("rem.send")}
      className={`inline-flex items-center gap-1.5 border font-mono uppercase tracking-[0.25em] transition-all disabled:opacity-50 ${sizeCls} ${className}`}
      style={{
        color: accent,
        borderColor: accent,
        backgroundColor: "transparent",
      }}
      onMouseOver={(e) => {
        if (!busy) {
          e.currentTarget.style.backgroundColor = accent;
          e.currentTarget.style.color = "white";
        }
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
        e.currentTarget.style.color = accent;
      }}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
      <span>{busy ? t("rem.sending") : `${t("rem.send")} · L${nextLevel}`}</span>
    </button>
  );
}
